import { type ReactNode } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HOST, LEGAL_ENTITY } from "@/lib/legalEntity";

// Mentions légales (LCEN Art. 6-III, #683). Meta tags come from
// MarketingController; the facts from lib/legalEntity.
const LegalNotice = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const publisher: [string, ReactNode][] = [
    [t("legalNotice.companyName"), LEGAL_ENTITY.name],
    [t("legalNotice.legalForm"), LEGAL_ENTITY.legalForm],
    [t("legalNotice.shareCapital"), LEGAL_ENTITY.shareCapital],
    [t("legalNotice.registration"), LEGAL_ENTITY.registration],
    ["SIRET", LEGAL_ENTITY.siret],
    [t("legalNotice.vatNumber"), LEGAL_ENTITY.vatNumber],
    [t("legalNotice.registeredOffice"), LEGAL_ENTITY.address],
    [
      t("legalNotice.phone"),
      <a key="phone" href={`tel:${LEGAL_ENTITY.phone.replace(/\s/g, "")}`} className="underline">
        {LEGAL_ENTITY.phone}
      </a>,
    ],
    [
      t("legalNotice.email"),
      <a key="email" href={`mailto:${LEGAL_ENTITY.email}`} className="underline">
        {LEGAL_ENTITY.email}
      </a>,
    ],
    [t("legalNotice.publicationDirector"), LEGAL_ENTITY.publicationDirector],
  ];

  const host: [string, ReactNode][] = [
    [t("legalNotice.companyName"), HOST.name],
    [t("legalNotice.address"), HOST.address],
    [t("legalNotice.phone"), HOST.phone],
    [
      t("legalNotice.website"),
      <a key="website" href={HOST.website} className="underline" rel="noopener noreferrer">
        fly.io
      </a>,
    ],
  ];

  const rows = (items: [string, ReactNode][]) => (
    <dl className="not-prose grid grid-cols-1 sm:grid-cols-[minmax(0,14rem)_1fr] gap-x-6 gap-y-1 sm:gap-y-3 text-sm text-muted-foreground">
      {items.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="font-medium text-foreground">{label}</dt>
          <dd className="mb-3 sm:mb-0 break-words">{value}</dd>
        </div>
      ))}
    </dl>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container mx-auto px-4 py-24 max-w-3xl">
        <Button variant="ghost" size="sm" className="mb-6 -ml-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="w-4 h-4 mr-1" />
          {t("legalNotice.back")}
        </Button>
        <h1 className="text-3xl font-display font-bold text-foreground mb-6">{t("legalNotice.title")}</h1>
        <div className="prose text-muted-foreground space-y-4 text-sm">
          <p>{t("legalNotice.intro")}</p>

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("legalNotice.publisherTitle")}</h2>
          {rows(publisher)}

          <h2 className="text-lg font-semibold text-foreground mt-6">{t("legalNotice.hostTitle")}</h2>
          {rows(host)}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default LegalNotice;
